'use client';

import { CellMeasurerCache, createMasonryCellPositioner, type Positioner } from 'react-virtualized';
import { BREAKPOINTS } from '@/app/constants';
import type { NewShowCaseList } from '@/app/types';

/**
 * 瀑布流常量
 */
export const MASONRY_CONSTANTS = {
  SPACING: 4,
  DEFAULT_CELL: {
    HEIGHT: 250,
    WIDTH: 300,
  },
} as const;

type ColumnsRule = ReadonlyArray<{ breakpoint: number; columns: number }>;

/**
 * 统一的列数规则配置
 *
 * 比较规则说明:
 * - 采用 "innerWidth < breakpoint" 的方式判断当前命中的规则
 * - 因此规则中应当填写该区间的最小值(*_MIN)
 */
export type ColumnsPreset = 'fewer' | 'default' | 'more' | 'activity';

const MASONRY_PRESETS: Record<ColumnsPreset, { rules: ColumnsRule; fallback: number }> = {
  // 更少列(原小瀑布流)
  fewer: {
    rules: [
      { breakpoint: BREAKPOINTS.DEFAULT_MIN, columns: 2 },
      { breakpoint: BREAKPOINTS.FHD_MIN, columns: 3 },
      { breakpoint: BREAKPOINTS.QHD_MIN, columns: 4 },
      { breakpoint: BREAKPOINTS.QHD_PLUS_MIN, columns: 5 },
    ],
    fallback: 6,
  },
  // 默认(原大瀑布流)
  default: {
    rules: [
      { breakpoint: BREAKPOINTS.LG_MIN, columns: 2 },
      { breakpoint: BREAKPOINTS.DEFAULT_MIN, columns: 3 },
      { breakpoint: BREAKPOINTS.QHD_MIN, columns: 4 },
      { breakpoint: BREAKPOINTS.QHD_PLUS_MIN, columns: 5 },
    ],
    fallback: 6,
  },
  // 更多列
  more: {
    rules: [
      { breakpoint: BREAKPOINTS.MD_MIN, columns: 2 },
      { breakpoint: BREAKPOINTS.LG_MIN, columns: 3 },
      { breakpoint: BREAKPOINTS.DEFAULT_MIN, columns: 4 },
      { breakpoint: BREAKPOINTS.QHD_MIN, columns: 5 },
      { breakpoint: BREAKPOINTS.QHD_PLUS_MIN, columns: 6 },
    ],
    fallback: 7,
  },
  // 活动详情页(最多 4 列)
  activity: {
    rules: [
      { breakpoint: BREAKPOINTS.MD_MIN, columns: 2 },
      { breakpoint: BREAKPOINTS.LG_MIN, columns: 3 },
      { breakpoint: BREAKPOINTS.DEFAULT_MIN, columns: 4 },
    ],
    fallback: 4,
  },
} as const;

// 将工具函数抽离到组件外部
export const MARK_PERCENT_MAP = new Map<number, string>([
  [1, '28%'],
  [9, '16%'],
  [16, '50%'],
]);

/**
 * 创建缓存配置
 */
export const createCache = ({
  width,
  columnsPreset = 'default',
}: {
  width?: number;
  columnsPreset?: ColumnsPreset;
}): CellMeasurerCache => {
  return new CellMeasurerCache({
    defaultHeight: MASONRY_CONSTANTS.DEFAULT_CELL.HEIGHT,
    defaultWidth: width ? calculateColumnWidth({ width, columnsPreset }) : MASONRY_CONSTANTS.DEFAULT_CELL.WIDTH,
    fixedWidth: true,
    keyMapper: (index) => index.toString(),
  });
};

/**
 * 计算瀑布流列数
 */
export const calculateColumnCount = ({ columnsPreset = 'default' }: { columnsPreset?: ColumnsPreset }): number => {
  const innerWidth = window.innerWidth;
  const { rules, fallback } = MASONRY_PRESETS[columnsPreset];
  for (const { breakpoint, columns } of rules) {
    if (innerWidth < breakpoint) return Math.max(1, columns);
  }
  return Math.max(1, fallback);
};

/**
 * 计算瀑布流列宽
 */
export const calculateColumnWidth = ({
  width,
  columnsPreset = 'default',
  spacer = MASONRY_CONSTANTS.SPACING,
}: {
  width: number;
  columnsPreset?: ColumnsPreset;
  spacer?: number;
}): number => {
  const columnCount = calculateColumnCount({ columnsPreset });
  return Math.floor((width - (columnCount - 1) * spacer) / columnCount);
};

/**
 * 创建瀑布流定位器
 */
export const createPositioner = ({
  width,
  cache,
  columnsPreset = 'default',
  spacer = MASONRY_CONSTANTS.SPACING,
}: {
  width: number;
  cache: CellMeasurerCache;
  columnsPreset?: ColumnsPreset;
  spacer?: number;
}): Positioner => {
  const columnWidth = calculateColumnWidth({ width, columnsPreset });
  return createMasonryCellPositioner({
    cellMeasurerCache: cache,
    columnCount: calculateColumnCount({ columnsPreset }),
    columnWidth,
    spacer: spacer,
  });
};

/**
 * 获取高度百分比
 */
export const getPercentage = (width: number | undefined): string => {
  if (width === undefined) return '50%';
  return MARK_PERCENT_MAP.get(width) ?? '50%';
};

/**
 * 检查行是否已加载
 */
export const isRowLoaded = (inspAssets: NewShowCaseList) => {
  return ({ index }: { index: number }) => Boolean(inspAssets[index]);
};

// 导出类型
export type { Positioner, CellMeasurerCache };
